<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Reactivate</div>

            <form novalidate @submit.prevent="createScheduleReactivation">
                <LsDatepicker
                    v-model="start_date"
                    track-id="reactivate_view-start_date"
                    label="Select date to activate service"
                    name="start_date"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :unselectable-days-of-week="unselectableDaysOfWeek"
                    :validation="{ required: true }"
                    class="ls-flow--330"
                    @input="onDateChange"
                />

                <LsRadios
                    v-model="cycle"
                    track-id="reactivate_view-options"
                    name="cycle"
                    label="Frequency"
                    :options="frequencyOptions"
                    :validation="{ required: true }"
                />

                <div class="ls-reactivate-view--deal-container">
                    <LsInput
                        v-model="deal"
                        track-id="reactivate_view-deal"
                        label="Coupon code (Optional)"
                        name="deal"
                        class="ls-reactivate-view--deal"
                        placeholder="Enter code"
                    />
                    <LsButton
                        track-id="reactivate_view-apply_deal"
                        text="Apply"
                        :loading="isUpdatingDeal"
                        @click="applyDeal"
                    />
                </div>
                <LsDismissibleNotice
                    v-if="noticeData"
                    class="ls-reactivate-view--notice"
                    :left-image="noticeData.status"
                    :label="noticeData.label"
                    @click="clearDeal"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="reactivate_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="reactivate_view-confirm_reactivation"
                    text="Confirm Reactivation"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';

import { lsHelpers } from '@/helpers';
import { lsScheduleCycles } from '@/constants';
import { FlowSuccessModal } from '@/modals';
import { apolloClient } from '@/vue-apollo';
import { LsButton, LsRadios, LsInput, LsDatepicker } from '@/components/common';
import LsError from '@/components/common/LsError/LsError.vue';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsDismissibleNotice from '@/components/LsDismissibleNotice/LsDismissibleNotice.vue';

import Deal from '@/graphql/queries/Deal.gql';
import ScheduleReactivation from '@/graphql/queries/ScheduleReactivation.gql';
import Zip from '@/graphql/queries/Zip.gql';
import CreateScheduleReactivation from '@/graphql/mutations/CreateScheduleReactivation.gql';

export default {
    name: 'ReactivateView',
    components: {
        LsButton,
        LsDatepicker,
        LsRadios,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsDismissibleNotice,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isUpdatingDeal: false,
            isInvalid: false,

            options: [],
            monthly: {
                idx: null,
            },
            noticeData: null,
            dealData: {},
            isMonthlyMowingDisabled: false,
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                        zip
                    }
                `,
            }),

            // form data
            start_date: null,
            cycle: null,
            deal: '',
            zendesk_ticket_id: '',
            day_of_week_availability: {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: false,
                sunday: false,
            },
        };
    },
    computed: {
        minDate() {
            return moment
                .tz('America/Chicago')
                .startOf('day')
                .add(2, 'days')
                .toDate();
        },
        maxDate() {
            return moment
                .tz('America/Chicago')
                .startOf('day')
                .add(2, 'months')
                .toDate();
        },
        unselectableDaysOfWeek() {
            const unselectableDaysOfWeek = [];

            if (!this.day_of_week_availability.monday) {
                unselectableDaysOfWeek.push(1);
            }

            if (!this.day_of_week_availability.tuesday) {
                unselectableDaysOfWeek.push(2);
            }

            if (!this.day_of_week_availability.wednesday) {
                unselectableDaysOfWeek.push(3);
            }

            if (!this.day_of_week_availability.thursday) {
                unselectableDaysOfWeek.push(4);
            }

            if (!this.day_of_week_availability.friday) {
                unselectableDaysOfWeek.push(5);
            }

            if (!this.day_of_week_availability.saturday) {
                unselectableDaysOfWeek.push(6);
            }

            if (!this.day_of_week_availability.sunday) {
                unselectableDaysOfWeek.push(0);
            }
            return unselectableDaysOfWeek;
        },
        normalizedZip() {
            const zip = _.get(this.property, 'zip');
            return zip ? String(zip).slice(0, 5) : null;
        },
        frequencyOptions() {
            if (!this.options || !this.options.length) {
                return [];
            }

            return this.options.map((option) => {
                let isOptionDisabled =
                    option.cycle === lsScheduleCycles.monthly && this.isMonthlyMowingDisabled;

                return {
                    value: option.cycle,
                    label: `${lsFilters.frequency({
                        cycle: option.cycle,
                    })} - ${lsFilters.currency({
                        amount: option.total,
                    })}`,
                    subLabel: lsHelpers.pricingSubLabel(option),
                    disabled: isOptionDisabled,
                };
            });
        },
    },
    apollo: {
        scheduleReactivation: {
            query: ScheduleReactivation,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.options = data.scheduleReactivation.options;

                    if (this.frequencyOptions.length) {
                        this.cycle = this.frequencyOptions[0].value;

                        const monthlyIdx = _.findIndex(this.frequencyOptions, { value: 28 });
                        this.monthly.idx = monthlyIdx > -1 ? monthlyIdx : null;
                        if (!_.isNull(this.monthly.idx)) {
                            this.monthly.label = this.frequencyOptions[this.monthly.idx].label;
                            this.monthly.subLabel = this.frequencyOptions[
                                this.monthly.idx
                            ].subLabel;
                        }
                    }

                    this.seasons = data.scheduleReactivation.seasons;
                    this.day_of_week_availability =
                        data.scheduleReactivation.day_of_week_availability;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleReactivation.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
        },
        isMonthlyMowingDisabled: {
            query: Zip,
            client: 'lighthouse',
            variables() {
                return { zip: this.normalizedZip };
            },
            update({ getZip }) {
                const market = getZip ? getZip.market : null;
                const activeSeason = market ? market.activeSeason : null;
                return lsHelpers.isMonthlyMowingDisabled(activeSeason);
            },
            skip() {
                return !this.normalizedZip;
            },
            fetchPolicy: 'network-only',
        },
    },
    methods: {
        async createScheduleReactivation() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleReactivation,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        start_date: this.start_date,
                        cycle: this.cycle,
                        deal: this.dealData.id,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service reactivated for ${this.property.street}`,
                            copy: data.createScheduleReactivation.copy,
                            replaceRoute: true,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
        applyDeal() {
            if (!this.deal || this.deal === this.dealData.id) {
                return;
            }

            this.isUpdatingDeal = true;
            this.noticeData = null;
            this.dealData = {};
            this.$apollo
                .query({
                    query: Deal,
                    variables: {
                        id: this.deal,
                    },
                    fetchPolicy: 'no-cache',
                })
                .then(({ data: { deal } }) => {
                    this.dealData = deal;
                    this.noticeData = {
                        status: 'success',
                        label: `Coupon code applied - ${lsFilters.currency({
                            amount: deal.price_first_job || deal.credit_amount,
                            trimTrailingZeros: true,
                        })} ${deal.price_first_job ? 'first' : 'off'} mow`,
                    };
                })
                .catch((error) => {
                    if (error.message.includes('This code is not valid.')) {
                        this.noticeData = {
                            status: 'failure',
                            label: 'Invalid coupon code',
                        };
                    }

                    lsErrorService.log({ error, showAlert: !this.noticeData });
                })
                .finally(() => {
                    this.isUpdatingDeal = false;
                });
        },
        onDateChange() {
            // if monthly mowing is not an option, short circuit
            if (_.isNull(this.monthly.idx)) {
                return;
            }

            // reset the state
            this.handleMonthly({ disable: false });

            // loop through the returned seasons and check if currently selected date falls between
            // the monthly blockout dates of either the current season or the following season
            _.forEach(this.seasons, (season) => {
                if (
                    moment(this.start_date).isBetween(
                        season.mowing_monthly_disabled_starts_at,
                        season.mowing_monthly_disabled_ends_at
                    )
                ) {
                    // if so, disable monthly and reset the select to the first index
                    this.cycle = this.frequencyOptions[0].value;
                    this.handleMonthly({ disable: true });
                }
            });
        },
        handleMonthly({ disable }) {
            this.options[this.monthly.idx].disabled = disable;
            this.options[this.monthly.idx].label = disable ? 'Monthly (N/A)' : this.monthly.label;
            this.options[this.monthly.idx].subLabel = disable ? '-' : this.monthly.subLabel;
        },
        clearDeal() {
            this.deal = '';
            this.noticeData = null;
            this.dealData = {};
        },
    },
};
</script>

<style lang="scss">
.ls-reactivate-view--deal-container {
    display: flex;
    align-items: flex-end;
    margin-top: 20px;

    .ls-input--input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        width: 250px;
    }

    .button {
        min-width: 80px;
        height: 36px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
}

.ls-reactivate-view--notice {
    margin-top: -15px;
}
</style>
